import {
    OrderTransaction,
    OrderValidity,
    OrderMarketTime,
    OrderType,
    PriceType,
    AlgoStrategy,
    BondQuantityType,
} from '@orbis-systems/orbis-api-middleware';
import { getTradingFormMeta } from '@oneview/shared/TradingForm';
import { formatDate } from '@/utils/formatDate';
import type { TradingFormValues } from '@/components/~advisor/Trading/Trading/TradingForm/TradingFormSchema';
import { multiply } from 'lodash';

const API_DATE_WITH_TIME_AND_TIMEZONE = 'MM/dd/yyyy HH:mm:ss z';
const MARKET_TIMEZONE = 'America/New_York';

const getOptionSpreadTransaction = (action: OrderTransaction, legAction: OrderTransaction) => {
    if (action === OrderTransaction.Open) {
        if (legAction === OrderTransaction.Buy) {
            return OrderTransaction.BuyToOpen;
        } else if (legAction === OrderTransaction.Sell) {
            return OrderTransaction.SellToOpen;
        } else {
            return legAction;
        }
    } else {
        if (legAction === OrderTransaction.Buy) {
            return OrderTransaction.BuyToClose;
        } else if (legAction === OrderTransaction.Sell) {
            return OrderTransaction.SellToClose;
        } else {
            return legAction;
        }
    }
};

export function prepareOrder(
    values: TradingFormValues,
    meta: ReturnType<typeof getTradingFormMeta>,
    requireNbbo: boolean,
) {
    const {
        symbol,
        orderType,
        limitPrice,
        stopPrice,
        transaction,
        fillType,
        quantity,
        contracts,
        marketTime,
        validity,
        optionLegs,
        mutualFundValue,
        notionalValue,
        accountId,
        modelId,
        trailingStopPegType,
        trailingStopPegDifference,
        positionType,
        spreadPrice,
        price,
        ocoOrder,
        ocoQuantity,
        ocoSymbol,
        ocoLimitPrice,
        ocoStopPrice,
        algoStrategy,
        algoStartTime,
        algoEndTime,
        volumeConstraint,
        settlementCurrency,
        commission,
        commissionType,
        expireTime,
    } = values;

    const {
        modelTrading,
        inventoryTrading,
        optionTrading,
        spreadTrading,
        mutualFundTrading,
        notionalOrder,
        limitOrder,
        stopOrder,
        trailingStopOrder,
        marketOrder,
        buyWrite,
        bondTrading,
        buying,
        algoTrading,
        shorting,
        buyToCover,
    } = meta;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let orderBody: any;

    if (bondTrading) {
        orderBody = {
            account: {
                accountId,
            },
            requireNbbo,
            order: {
                quote: {
                    isin: symbol,
                },
                orderType: OrderType.Limit,
                quantityType: BondQuantityType.Unit,
                quantity,
                transaction,
                price,
                priceType: PriceType.Percentage,
                settlementCurrency,
                commission,
            },
        };
    } else if (mutualFundTrading) {
        orderBody = {
            account: {
                accountId,
            },
            requireNbbo,
            order: {
                quote: {
                    symbol,
                },
                transaction,
                settlementCurrency,
                value: mutualFundValue,
            },
        };
    } else if (buyWrite && contracts) {
        orderBody = {
            account: {
                accountId,
            },
            requireNbbo,
            order: {
                validity,
                orderType,
                legs: [
                    {
                        quantity: multiply(contracts, 100),
                        quote: {
                            symbol: symbol,
                            option: false,
                        },
                        transaction: OrderTransaction.Buy,
                    },
                    {
                        quantity: contracts,
                        quote: {
                            symbol: optionLegs[0]?.symbol,
                            option: true,
                        },
                        transaction: OrderTransaction.SellToOpen,
                    },
                ],
            },
        };
    } else if (spreadTrading) {
        orderBody = {
            account: {
                accountId,
            },
            requireNbbo,
            order: {
                validity,
                orderType,
                legs: optionLegs.map((leg) => {
                    return {
                        quantity: contracts,
                        quote: {
                            symbol: leg.symbol,
                            option: true,
                        },
                        transaction: getOptionSpreadTransaction(
                            values.transaction || OrderTransaction.Open,
                            leg.action || OrderTransaction.Buy,
                        ),
                    };
                }),
            },
        };

        if (!marketOrder) {
            orderBody.order.spreadPrice = spreadPrice;
        }
    } else {
        orderBody = {
            requireNbbo,
            order: {
                quote: {
                    symbol,
                    option: optionTrading,
                },
                marketTime,
                quantity,
                orderAccountType: positionType,
                transaction,
                orderType,
                fillType,
                validity,
                settlementCurrency,
                commission,
                commissionType,
            },
        };

        if (modelTrading) {
            orderBody.model = {
                id: modelId,
            };
        } else if (inventoryTrading) {
            orderBody.inventory = true;
        } else {
            orderBody.account = {
                accountId,
            };
        }

        if (optionTrading) {
            orderBody.order.quote.symbol = optionLegs[0]?.symbol;
            orderBody.order.marketTime = OrderMarketTime.CoreMarket;
            orderBody.order.quantity = contracts;
        }

        if (algoTrading) {
            orderBody.order.orderType = algoStrategy;
            orderBody.order.startTime = algoStartTime;
            orderBody.order.endTime = algoEndTime;

            if (algoStrategy !== AlgoStrategy.TWAP) {
                orderBody.order.volumeConstraint = volumeConstraint;
            }

            if (limitOrder) {
                orderBody.order.limitPrice = limitPrice;
            }
        }

        if (limitOrder) {
            orderBody.order.limitPrice = limitPrice;
        }

        if (stopOrder) {
            orderBody.order.stopPrice = stopPrice;
        }

        if (trailingStopOrder) {
            orderBody.order.pegType = trailingStopPegType;
            orderBody.order.pegDifference = trailingStopPegDifference;
        }

        // OCO orders are equity only
        if (ocoOrder) {
            orderBody.order.oco = {
                quote: {
                    // Symbol must match when selling oco
                    symbol: buying ? ocoSymbol : symbol,
                    option: optionTrading,
                },
                // Sells must be same quantity as main order
                quantity: buying ? ocoQuantity : quantity,
            };

            if (
                (limitOrder && transaction === OrderTransaction.Buy) ||
                (stopOrder && transaction === OrderTransaction.Sell)
            ) {
                orderBody.order.oco.limitPrice = ocoLimitPrice;
            }

            if (
                (stopOrder && transaction === OrderTransaction.Buy) ||
                (limitOrder && transaction === OrderTransaction.Sell)
            ) {
                orderBody.order.oco.stopPrice = ocoStopPrice;
            }
        }

        if (notionalOrder) {
            delete orderBody.order.quantity;
            orderBody.order.expectedValue = notionalValue;
        }

        if (shorting || buyToCover) {
            delete orderBody.order.orderAccountType;
        }

        if (values.validity === OrderValidity.GoodTilDate && expireTime) {
            orderBody.order.expireTime = formatDate(expireTime, {
                pattern: API_DATE_WITH_TIME_AND_TIMEZONE,
                timeZone: MARKET_TIMEZONE,
            });
        }
    }

    return orderBody;
}
